import {
  Box,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StringParam, useQueryParam } from "use-query-params";
import useDebounce from "../../hooks/useDebounce";
import Iconify from "../../minimals/components/iconify";
import Label from "../../minimals/components/label";
import ActiveStatusLabel from "../shared/ActiveStatusLabel";
import StickyTable from "../shared/StickyTable";
import { InternalUser, InternalUserDepartment, InternalUserRoleType, InternalUserTeam } from "../types/app";
import RoleOptionsDropdown from "./RoleOptionsDropdown";

export default function ManagersTable() {
  const [tableData, setTableData] = useState<InternalUser[]>([]);
  const [searchText, setSearchText] = useQueryParam("searchText", StringParam);
  const [hideInactive, setHideInactive] = useState<boolean>(true);
  const [teamOptions, setTeamOptions] = useState<InternalUserTeam[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<InternalUserDepartment[]>([]);
  const [roleTypeOptions, setRoleTypeOptions] = useState<InternalUserRoleType[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(0);
  const [selectedTeamId, setSelectedTeamId] = useState<number>(0);
  const [selectedRoleTypeId, setSelectedRoleTypeId] = useState<number>(0);
  const debouncedSearchText = useDebounce(searchText || "", 500);
  const navigate = useNavigate();

  const headers = ["User", "Email", "Role @ Department - Team", "Active", "Action"];

  const hasFilters = selectedDepartmentId !== 0 || selectedTeamId !== 0 || selectedRoleTypeId !== 0;

  const searchInternalUsersWithRoles = async (
    currentSearchText: string,
    departmentId: number,
    teamId: number,
    roleTypeId: number
  ) => {
    const params = new URLSearchParams();
    if (currentSearchText.trim()) {
      params.set("searchText", currentSearchText);
    }
    if (departmentId !== 0) {
      params.set("internalUserDepartmentId", departmentId.toString());
    }
    if (teamId !== 0) {
      params.set("internalUserTeamId", teamId.toString());
    }
    if (roleTypeId !== 0) {
      params.set("internalUserRoleTypeId", roleTypeId.toString());
    }

    const { data } = await axios.get<InternalUser[]>(`/api/clientphonebook/internalusers/searchWithRoles?${params.toString()}`);
    setTableData(data);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  useEffect(() => {
    if (debouncedSearchText || hasFilters) {
      searchInternalUsersWithRoles(
        debouncedSearchText,
        selectedDepartmentId,
        selectedTeamId,
        selectedRoleTypeId
      );
    } else {
      setTableData([]);
    }
  }, [debouncedSearchText, hasFilters, selectedDepartmentId, selectedRoleTypeId, selectedTeamId]);

  useEffect(() => {
    const getTeamsDeptsAndRoles = async () => {
      const { data: roleOpts } = await axios.get<InternalUserRoleType[]>(
        `/api/clientphonebook/internalusers/getInternalUserRoleTypeOptions`
      );
      setRoleTypeOptions(roleOpts);

      const { data: teamOpts } = await axios.get<InternalUserTeam[]>(
        `/api/clientphonebook/internalusers/getInternalUserTeamOptions`
      );
      setTeamOptions(teamOpts);

      const { data: deptOptions } = await axios.get<InternalUserDepartment[]>(
        `/api/clientphonebook/internalusers/getInternalUserDepartmentOptions`
      );
      setDepartmentOptions(deptOptions);
    };

    getTeamsDeptsAndRoles();
  }, []);

  const renderTableBody = () => {
    if (!debouncedSearchText && !hasFilters) {
      return (
        <TableRow>
          <TableCell colSpan={headers.length} align="center">
            <Typography variant="subtitle2">Please type to search or choose a filter</Typography>
          </TableCell>
        </TableRow>
      );
    }
    if (!!debouncedSearchText && tableData.length == 0) {
      return (
        <TableRow>
          <TableCell colSpan={headers.length} align="center">
            <Typography variant="subtitle2">No search results found</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return tableData
      .filter((d) => (hideInactive ? d.isActiveSelectUser : d))
      .map((row) => {
        const { id, fullName, email, internalUserRoles, isActiveSelectUser } = row;
        return (
          <TableRow key={id}>
            <TableCell>{fullName}</TableCell>
            <TableCell>{email?.toLowerCase()}</TableCell>
            <TableCell>
              {internalUserRoles?.map((role, index) => (
                <Label key={index} marginRight={2} variant="soft" color="success">
                  {role?.internalUserRoleType?.name} @ {role?.internalUserDepartment?.name} -{" "}
                  {role?.internalUserTeam?.name}
                </Label>
              ))}
            </TableCell>
            <TableCell>
              <ActiveStatusLabel isActive={isActiveSelectUser} />
            </TableCell>
            <TableCell>
              <IconButton
                onClick={() => {
                  navigate(`/internal-user-roles/edit/${id}`);
                }}
              >
                <Iconify icon="mdi:lead-pencil" />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      });
  };

  return (
    <>
      <Typography variant="h4" pb={2}>
        User Roles, Teams, and Departments
      </Typography>
      <Card>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                )
              }}
            ></TextField>
            <RoleOptionsDropdown
              sx={{ minWidth: 220 }}
              label="Department"
              value={selectedDepartmentId}
              options={departmentOptions}
              onChange={(e) => setSelectedDepartmentId(Number(e.target.value))}
            />
            <RoleOptionsDropdown
              sx={{ minWidth: 220 }}
              label="Team"
              value={selectedTeamId}
              options={teamOptions}
              onChange={(e) => setSelectedTeamId(Number(e.target.value))}
            />
            <RoleOptionsDropdown
              sx={{ minWidth: 220 }}
              label="Role"
              value={selectedRoleTypeId}
              options={roleTypeOptions}
              onChange={(e) => setSelectedRoleTypeId(Number(e.target.value))}
            />
            <FormControlLabel
              control={<Checkbox checked={hideInactive} onChange={(e, value) => setHideInactive(value)} />}
              label="Hide Inactive"
            />
          </Stack>
        </Box>

        <TableContainer sx={{ overflow: "unset" }}>
          <StickyTable sx={{ minWidth: 720, textAlign: "left" }}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{renderTableBody()}</TableBody>
          </StickyTable>
        </TableContainer>
        <Divider />
        <Box sx={{ p: 2, textAlign: "right", alignItems: "center" }}>
          <Switch /> Dense
        </Box>
      </Card>
    </>
  );
}
